import React from 'react'
import { Dialog, DialogContent, Box, Typography, makeStyles } from '@material-ui/core'
import { WorkawareApi } from 'apis/WorkawareApi'
import userManager from 'utils/userManager'

const DEFAULT_MESSAGE =
  "You will be signed out soon due to inactivity.\n" +
  "Your session will end in <countdown_timer>.\n" +
  "Select 'Stay signed in' to continue working without interruption."

// sessionStorage keys — scoped per browser tab, survive page refresh, cleared on tab close
const LAST_ACTIVITY_KEY = 'workaware-session-last-activity'
const WARNING_SHOWN_KEY  = 'workaware-session-warning-shown'

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: '12px',
    padding: '8px',
    maxWidth: '420px',
    width: '100%',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '24px !important',
  },
  iconRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    width: '100%',
  },
  warningIcon: {
    fontSize: '28px',
    flexShrink: 0,
    marginTop: '2px',
  },
  textBlock: { flex: 1 },
  message: {
    fontSize: '14px',
    color: theme.palette.text.primary,
    lineHeight: '1.6',
    whiteSpace: 'pre-line' as const,
  },
  countdown: {
    fontSize: '14px',
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  actions: {
    display: 'flex',
    gap: '10px',
    marginTop: '20px',
    width: '100%',
    justifyContent: 'flex-end',
  },
  btnPrimary: {
    padding: '8px 20px',
    fontSize: '13px',
    fontWeight: 600,
    borderRadius: '6px',
    border: 'none',
    background: theme.palette.primary.main,
    color: '#fff',
    cursor: 'pointer',
    '&:hover': { opacity: 0.9 },
  },
  btnSecondary: {
    padding: '8px 20px',
    fontSize: '13px',
    fontWeight: 500,
    borderRadius: '6px',
    border: `1px solid ${theme.palette.divider}`,
    background: 'transparent',
    color: theme.palette.text.primary,
    cursor: 'pointer',
    '&:hover': { background: theme.palette.action.hover },
  },
}))

const performLogout = async () => {
  // Clear session state before redirecting so a subsequent login starts clean
  sessionStorage.removeItem(LAST_ACTIVITY_KEY)
  sessionStorage.removeItem(WARNING_SHOWN_KEY)
  localStorage.removeItem('workaware-persisted-user')
  localStorage.removeItem('workaware-remember-me')
  await userManager.removeUser()
  window.location.replace('/')
}

interface Settings {
  isEnabled: boolean
  inactivityMs: number
  warningEnabled: boolean
  warningMs: number
  message: string
}

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click']

const SessionTimeoutManager: React.FC = () => {
  const classes = useStyles()
  const [showWarning, setShowWarning] = React.useState(false)
  const [countdown, setCountdown] = React.useState(0)
  const settingsRef = React.useRef<Settings | null>(null)
  const inactivityTimerRef  = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningTimerRef     = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const warningShownRef = React.useRef(false)

  const clearAllTimers = () => {
    if (inactivityTimerRef.current)   { clearTimeout(inactivityTimerRef.current);   inactivityTimerRef.current   = null }
    if (warningTimerRef.current)      { clearTimeout(warningTimerRef.current);       warningTimerRef.current      = null }
    if (countdownIntervalRef.current) { clearInterval(countdownIntervalRef.current); countdownIntervalRef.current = null }
  }

  const startCountdown = (seconds: number) => {
    setCountdown(seconds)
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(countdownIntervalRef.current!); return 0 }
        return prev - 1
      })
    }, 1000)
  }

  // Show the warning dialog and start the logout countdown.
  // remainingWarningMs: how many ms are left in the warning period
  //   (normally = warningMs, but after a refresh it may be less)
  const showWarningDialog = React.useCallback((remainingWarningMs: number) => {
    warningShownRef.current = true
    setShowWarning(true)
    startCountdown(Math.round(remainingWarningMs / 1000))
    warningTimerRef.current = setTimeout(async () => {
      sessionStorage.removeItem(WARNING_SHOWN_KEY)
      await performLogout()
    }, remainingWarningMs)
  }, [])

  // Trigger the warning for the first time (full warning period).
  const triggerWarning = React.useCallback(() => {
    if (warningShownRef.current) return
    // Persist the moment the warning was shown so a page refresh can resume correctly.
    sessionStorage.setItem(WARNING_SHOWN_KEY, Date.now().toString())
    const s = settingsRef.current!
    showWarningDialog(s.warningMs)
  }, [showWarningDialog])

  // (Re-)start the inactivity countdown from now.
  const resetTimers = React.useCallback(() => {
    const s = settingsRef.current
    if (!s || !s.isEnabled) return

    clearAllTimers()
    warningShownRef.current = false

    if (s.inactivityMs <= 0) return

    if (s.warningEnabled) {
      inactivityTimerRef.current = setTimeout(() => triggerWarning(), s.inactivityMs)
    } else {
      inactivityTimerRef.current = setTimeout(() => performLogout(), s.inactivityMs)
    }
  }, [triggerWarning])

  // Re-fetch settings and restart timers when the admin saves new settings in the same session.
  // SessionTimeoutTab dispatches 'workaware:sessionSettingsChanged' after a successful save.
  const reloadSettings = React.useCallback(async () => {
    try {
      const api = new WorkawareApi()
      const data = await api.user.getSessionTimeoutSettings()
      if (!data || !data.IsEnabled) {
        clearAllTimers()
        settingsRef.current = null
        setShowWarning(false)
        setCountdown(0)
        warningShownRef.current = false
        return
      }
      const warningMs = data.LogoutWarningEnabled ? data.WarningTimerMinutes * 60 * 1000 : 0
      settingsRef.current = {
        isEnabled: data.IsEnabled,
        inactivityMs: data.InactivityTimeoutMinutes * 60 * 1000,
        warningEnabled: data.LogoutWarningEnabled,
        warningMs,
        message: data.CustomMessageEnabled && data.CustomMessage
          ? data.CustomMessage
          : DEFAULT_MESSAGE,
      }
      sessionStorage.removeItem(WARNING_SHOWN_KEY)
      sessionStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString())
      setShowWarning(false)
      setCountdown(0)
      warningShownRef.current = false
      clearAllTimers()
      resetTimers()
    } catch {
      // Non-fatal
    }
  }, [resetTimers])

  React.useEffect(() => {
    window.addEventListener('workaware:sessionSettingsChanged', reloadSettings)
    return () => window.removeEventListener('workaware:sessionSettingsChanged', reloadSettings)
  }, [reloadSettings])

  // Load settings once on mount, then restore state based on stored timestamps.
  React.useEffect(() => {
    let cancelled = false
    // Nudged slightly after mount so it doesn't compete with the current page's own
    // data fetch during the first-load burst of app-shell requests. Negligible against
    // the minute-scale inactivity/warning thresholds this drives.
    const startTimer = setTimeout(() => {
    if (cancelled) return
    ;(async () => {
      try {
        const api = new WorkawareApi()
        const data = await api.user.getSessionTimeoutSettings()
        if (cancelled || !data || !data.IsEnabled) return

        const warningMs = data.LogoutWarningEnabled ? data.WarningTimerMinutes * 60 * 1000 : 0

        settingsRef.current = {
          isEnabled: data.IsEnabled,
          inactivityMs: data.InactivityTimeoutMinutes * 60 * 1000,
          warningEnabled: data.LogoutWarningEnabled,
          warningMs,
          message: data.CustomMessageEnabled && data.CustomMessage
            ? data.CustomMessage
            : DEFAULT_MESSAGE,
        }

        const s = settingsRef.current
        const now = Date.now()

        // ── Restore state after a page refresh ──────────────────────────────
        const storedWarningAt  = parseInt(sessionStorage.getItem(WARNING_SHOWN_KEY)  || '0') || 0
        const storedLastActive = parseInt(sessionStorage.getItem(LAST_ACTIVITY_KEY)  || '0') || 0

        if (storedWarningAt > 0) {
          // Warning was already showing before the refresh.
          const elapsedSinceWarning = now - storedWarningAt
          if (elapsedSinceWarning >= s.warningMs) {
            // Warning period has fully elapsed while the page was reloading → logout.
            sessionStorage.removeItem(WARNING_SHOWN_KEY)
            await performLogout()
          } else {
            // Resume the warning dialog with whatever time is left.
            warningShownRef.current = true
            if (!cancelled) {
              setShowWarning(true)
              showWarningDialog(s.warningMs - elapsedSinceWarning)
            }
          }
          return
        }

        if (storedLastActive > 0) {
          // No warning yet — check if inactivity threshold was already passed.
          const inactiveDuration = now - storedLastActive
          if (inactiveDuration >= s.inactivityMs) {
            // Threshold exceeded during the refresh. Trigger warning or logout directly.
            if (s.warningEnabled) {
              triggerWarning()
            } else {
              await performLogout()
            }
            return
          }
          // Still within the inactivity window — start a timer for the remainder.
          const remaining = s.inactivityMs - inactiveDuration
          if (s.warningEnabled) {
            inactivityTimerRef.current = setTimeout(() => triggerWarning(), remaining)
          } else {
            inactivityTimerRef.current = setTimeout(() => performLogout(), remaining)
          }
        } else {
          // First time this session — record now as the baseline and start normally.
          sessionStorage.setItem(LAST_ACTIVITY_KEY, now.toString())
          resetTimers()
        }
      } catch {
        // Non-fatal: session timeout unavailable — skip silently
      }
    })()
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(startTimer)
      clearAllTimers()
    }
    // eslint-disable-next-line
  }, [])

  // Attach/detach activity listeners
  React.useEffect(() => {
    const handleActivity = () => {
      if (warningShownRef.current) return // warning is open — don't reset
      // Update persisted timestamp so a refresh knows when the user was last active.
      sessionStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString())
      resetTimers()
    }

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }))
    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity))
      clearAllTimers()
    }
    // eslint-disable-next-line
  }, [resetTimers])

  const handleStaySignedIn = () => {
    sessionStorage.removeItem(WARNING_SHOWN_KEY)
    sessionStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString())
    setShowWarning(false)
    setCountdown(0)
    clearAllTimers()
    warningShownRef.current = false
    resetTimers()
  }

  const handleSignOutNow = () => {
    clearAllTimers()
    performLogout()
  }

  const s = settingsRef.current
  const rawMessage = s?.message || DEFAULT_MESSAGE
  const displayMessage = rawMessage
    .replace('<countdown_timer>', `${countdown}s`)
    .replace('<countdown_signout_timer>', `${countdown}s`)

  return (
    <Dialog
      open={showWarning}
      disableBackdropClick
      disableEscapeKeyDown
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogContent className={classes.content}>
        <Box className={classes.iconRow}>
          <span className={classes.warningIcon} role="img" aria-label="warning">⚠️</span>
          <Box className={classes.textBlock}>
            <Typography className={classes.message}>
              {displayMessage.split('\n').map((line, i) => {
                const tag = `${countdown}s`
                if (line.includes(tag)) {
                  const parts = line.split(tag)
                  return (
                    <span key={i}>
                      {parts[0]}
                      <span className={classes.countdown}>{countdown}s</span>
                      {parts[1]}
                      <br />
                    </span>
                  )
                }
                return <span key={i}>{line}<br /></span>
              })}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.actions}>
          <button className={classes.btnSecondary} onClick={handleSignOutNow}>
            Sign out now
          </button>
          <button className={classes.btnPrimary} onClick={handleStaySignedIn}>
            Stay signed in
          </button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default SessionTimeoutManager
